import type { SiteMode } from './detect-site-mode';

export interface RouteLocation {
  pathname: string;
  search: string;
  hash: string;
}

const desktopExactRoutes = new Set([
  '/',
  '/login',
  '/membership',
  '/membership/orders',
  '/video-management',
  '/digital-assets',
  '/profile-management',
  '/credit-consumption',
  '/data-report',
  '/video-generation',
  '/personal-management',
  '/user-management',
  '/quota-management',
  '/admin/video-jobs',
  '/admin/membership',
  '/admin/credit-codes',
  '/admin/prompt-templates',
  '/admin/prompt-evaluations',
  '/admin/prompt-evaluations/new',
  '/admin/prompt-evaluations/compare',
  '/admin/organ-libraries',
  '/admin/organ-image-learning',
  '/hospital-quota-management',
  '/user-quota-management',
  '/digital-human',
  '/help',
  '/about',
  '/privacy',
  '/terms',
  '/paid-services',
]);

const desktopDynamicRoutes = [
  /^\/profile\/[A-Za-z0-9_-]+$/,
  /^\/video-detail\/[A-Za-z0-9_-]+$/,
  /^\/video-edit\/[A-Za-z0-9_-]+$/,
  /^\/admin\/prompt-evaluations\/[A-Za-z0-9_-]+(?:\/review)?$/,
];

function splitRoute(rawRoute: string): { path: string; search: string } {
  const queryIndex = rawRoute.indexOf('?');
  if (queryIndex < 0) {
    return { path: rawRoute || '/', search: '' };
  }
  return {
    path: rawRoute.slice(0, queryIndex) || '/',
    search: rawRoute.slice(queryIndex),
  };
}

function mergeSearch(...searchParts: string[]): string {
  const result = new URLSearchParams();
  for (const search of searchParts) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    params.forEach((value, key) => result.append(key, value));
  }
  const serialized = result.toString();
  return serialized ? `?${serialized}` : '';
}

function desktopHashRoute(locationLike: RouteLocation): {
  path: string;
  search: string;
  original: string;
} {
  const rawHash = locationLike.hash.startsWith('#')
    ? locationLike.hash.slice(1)
    : locationLike.hash;
  const { path, search } = splitRoute(rawHash || '/');
  const mergedSearch = mergeSearch(locationLike.search, search);
  return {
    path: path.startsWith('/') ? path : `/${path}`,
    search: mergedSearch,
    original: `/#${path.startsWith('/') ? path : `/${path}`}${mergedSearch}`,
  };
}

export function mobilePublicVideoRoute(videoId: string | number): string {
  return `/video/${encodeURIComponent(String(videoId))}`;
}

export function mobilePublicProfileRoute(userId: string | number): string {
  return `/profile/${encodeURIComponent(String(userId))}`;
}

/**
 * Public pages only live in the mobile application. Legacy desktop hash URLs
 * are translated to their clean m-site routes, while an existing m-site
 * public URL is returned unchanged so UA detection cannot redirect it back to
 * www and create a loop.
 */
export function responsivePublicMobileTarget(
  currentSite: SiteMode,
  locationLike: RouteLocation,
): string | null {
  if (currentSite === 'desktop') {
    const route = desktopHashRoute(locationLike);
    const profileMatch = route.path.match(/^\/profile\/([A-Za-z0-9_-]+)$/);
    if (profileMatch) {
      return `${mobilePublicProfileRoute(profileMatch[1])}${route.search}`;
    }
    const videoMatch = route.path.match(/^\/video-detail\/([A-Za-z0-9_-]+)$/);
    if (videoMatch) {
      return `${mobilePublicVideoRoute(videoMatch[1])}${route.search}`;
    }
    return null;
  }

  if (
    /^\/profile\/[A-Za-z0-9_-]+$/.test(locationLike.pathname) ||
    /^\/video\/[A-Za-z0-9_-]+$/.test(locationLike.pathname)
  ) {
    return `${locationLike.pathname}${locationLike.search}`;
  }
  return null;
}

export function isAllowedDesktopTarget(rawTarget: string): boolean {
  if (
    !rawTarget.startsWith('/#/') ||
    rawTarget.length > 2048 ||
    rawTarget.includes('\\') ||
    rawTarget.startsWith('//#')
  ) {
    return false;
  }
  const hashRoute = rawTarget.slice(2);
  const { path } = splitRoute(hashRoute);
  return (
    desktopExactRoutes.has(path) ||
    desktopDynamicRoutes.some((pattern) => pattern.test(path))
  );
}

export function desktopToMobile(locationLike: RouteLocation): string {
  const route = desktopHashRoute(locationLike);
  const exactMap: Record<string, string> = {
    '/': '/create',
    '/login': '/login',
    '/terms': '/terms',
    '/privacy': '/privacy',
    '/paid-services': '/paid-services',
    '/membership': '/membership',
    '/membership/orders': '/membership/orders',
    '/video-management': '/works',
    '/digital-assets': '/digital-humans',
    '/profile-management': '/me/profile',
    '/credit-consumption': '/me/credits',
    '/data-report': '/me/overview',
  };
  if (exactMap[route.path]) {
    return `${exactMap[route.path]}${route.search}`;
  }

  const profileMatch = route.path.match(/^\/profile\/([A-Za-z0-9_-]+)$/);
  if (profileMatch) {
    return `/profile/${profileMatch[1]}${route.search}`;
  }
  const videoMatch = route.path.match(/^\/video-detail\/([A-Za-z0-9_-]+)$/);
  if (videoMatch) {
    return `/video/${videoMatch[1]}${route.search}`;
  }

  return `/desktop-required?target=${encodeURIComponent(route.original)}`;
}

export function mobileToDesktop(locationLike: RouteLocation): string {
  const exactMap: Record<string, string> = {
    '/create': '/',
    '/login': '/login',
    '/terms': '/terms',
    '/privacy': '/privacy',
    '/paid-services': '/paid-services',
    '/membership': '/membership',
    '/membership/orders': '/membership/orders',
    '/works': '/video-management',
    '/digital-humans': '/digital-assets',
    '/me': '/profile-management',
    '/me/profile': '/profile-management',
    '/me/credits': '/credit-consumption',
    '/me/overview': '/data-report',
  };

  if (locationLike.pathname === '/desktop-required') {
    const requestedTarget = new URLSearchParams(locationLike.search).get('target');
    if (requestedTarget && isAllowedDesktopTarget(requestedTarget)) {
      return requestedTarget;
    }
    return '/#/';
  }
  if (exactMap[locationLike.pathname]) {
    return `/#${exactMap[locationLike.pathname]}${locationLike.search}`;
  }

  const profileMatch = locationLike.pathname.match(/^\/profile\/([A-Za-z0-9_-]+)$/);
  if (profileMatch) {
    return `/#/profile/${profileMatch[1]}${locationLike.search}`;
  }
  const videoMatch = locationLike.pathname.match(/^\/video\/([A-Za-z0-9_-]+)$/);
  if (videoMatch) {
    return `/#/video-detail/${videoMatch[1]}${locationLike.search}`;
  }
  return '/#/';
}

export function mapRoute(
  currentSite: SiteMode,
  locationLike: RouteLocation,
): string {
  return currentSite === 'desktop'
    ? desktopToMobile(locationLike)
    : mobileToDesktop(locationLike);
}
